import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Typography,
  Avatar,
  CircularProgress,
  Fab,
  Tooltip,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { chatbotKnowledge } from './knowledge';

const FleetChatbot = () => {
  const dragContainerRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  // Tracks whether the user is currently dragging so click doesn't fire after drag
  const isDragging = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: 'Hello! I am your Fleet Analysis Assistant. I can help you understand your metrics or guide you through this analysis. Would you like a walkthrough?',
      timestamp: new Date(),
      actions: ['Start Walkthrough', 'Explain Metrics']
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const messagesEndRef = useRef(null);

  const handleAction = (action) => {
    if (action === 'Start Walkthrough') {
      startWalkthrough();
    } else {
      handleSend(action);
    }
  };

  const startWalkthrough = () => {
    const firstStep = chatbotKnowledge.walkthrough[0];
    setMessages(prev => [...prev,
    { role: 'user', content: 'Start Walkthrough', timestamp: new Date() },
    {
      role: 'bot',
      content: firstStep.content,
      timestamp: new Date(),
      actions: [`Next: ${chatbotKnowledge.walkthrough[1].title}`, 'Stop Walkthrough']
    }
    ]);
    setWalkthroughStep(1);
  };

  const nextWalkthroughStep = (stepIndex) => {
    const step = chatbotKnowledge.walkthrough[stepIndex];
    const isLast = stepIndex === chatbotKnowledge.walkthrough.length - 1;

    setMessages(prev => [...prev,
    {
      role: 'bot',
      content: step.content,
      timestamp: new Date(),
      actions: isLast ? ['Stop Walkthrough'] : [`Next: ${chatbotKnowledge.walkthrough[stepIndex + 1].title}`, 'Stop Walkthrough']
    }
    ]);
    setWalkthroughStep(stepIndex + 1);
  };


  const handleSend = async (customContent) => {
    const content = typeof customContent === 'string' ? customContent : inputValue;
    if (!content.trim()) return;

    // Check if it's a walkthrough navigation
    if (content.startsWith('Next: ')) {
      const nextTitle = content.replace('Next: ', '');
      const nextIndex = chatbotKnowledge.walkthrough.findIndex(s => s.title === nextTitle);
      if (nextIndex !== -1) {
        nextWalkthroughStep(nextIndex);
        return;
      }
    }

    if (content === 'Stop Walkthrough') {
      setMessages(prev => [...prev, { role: 'user', content: 'Stop Walkthrough', timestamp: new Date() }, { role: 'bot', content: 'Alright! Let me know if you need anything else.', timestamp: new Date() }]);
      setWalkthroughStep(0);
      return;
    }

    const userMessage = {
      role: 'user',
      content: content,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot response with knowledge base
    setTimeout(() => {
      const lowerContent = content.toLowerCase();

      // Special handling for metrics overview
      if (lowerContent.includes('metrics') || lowerContent === 'explain metrics') {
        const { metrics } = chatbotKnowledge;
        const metricsContent = `Here are the key fleet analysis benchmarks:
• Success Rate: ${metrics.success_rate} for presentations using analysis.
• Sweet Spot: ${metrics.sweet_spot_miles.toLocaleString()} miles for light-duty trucks.
• Maintenance: ${metrics.maintenance_tier_1} (0-50k), ${metrics.maintenance_tier_2} (50-100k), ${metrics.maintenance_tier_3} (100k+).
• Fuel: Engines lose ~${metrics.fuel_degradation} efficiency per year.
• Cycling: Managed fleets cycle every ${metrics.managed_cycle} vs ${metrics.unmanaged_cycle} for unmanaged.`;

        setMessages((prev) => [...prev, {
          role: 'bot',
          content: metricsContent,
          timestamp: new Date(),
          actions: ['Start Walkthrough']
        }]);
        setIsTyping(false);
        return;
      }

      const faq = chatbotKnowledge.faqs.find(f =>
        lowerContent.includes(f.question.toLowerCase().split(' ').slice(0, 3).join(' ')) ||
        f.question.toLowerCase().split(' ').some(word => word.length > 4 && lowerContent.includes(word)) ||
        (f.question.toLowerCase().includes('maintenance') && lowerContent.includes('repair')) ||
        (f.question.toLowerCase().includes('track lease') && lowerContent.includes('finance')) ||
        (f.question.toLowerCase().includes('sweet spot') && lowerContent.includes('mileage'))
      );

      let botResponse;
      if (faq) {
        botResponse = {
          role: 'bot',
          content: faq.answer,
          timestamp: new Date(),
          actions: faq.actions || ['Start Walkthrough', 'Explain Metrics']
        };
      } else {
        botResponse = {
          role: 'bot',
          content: `I'm still learning about "${content}". Based on the Fleet Analysis training, I can help with Fleet Cycling, Maintenance Analysis, and Fuel Savings. Would you like to see a walkthrough?`,
          timestamp: new Date(),
          actions: ['Start Walkthrough', 'Explain Metrics']
        };
      }

      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };


  return (
    <Box
      ref={dragContainerRef}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {/* Floating Action Button */}
      <motion.div
        drag
        dragConstraints={dragContainerRef}
        dragElastic={0.1}
        dragMomentum={false}
        onDragStart={() => {
          isDragging.current = true;
        }}
        onDragEnd={() => {
          // Keep the flag true briefly so the click event that fires
          // immediately after drag release is suppressed
          setTimeout(() => { isDragging.current = false; }, 100);
        }}
        style={{
          position: 'absolute',
          bottom: 60,
          left: 15,
          x,
          y,
          pointerEvents: 'auto',
          cursor: 'grab',
        }}
        whileTap={{ cursor: 'grabbing', scale: 0.95 }}
      >
        <Tooltip title="Drag to move | Click to chat" placement="right">
          <Fab
            color="primary"
            onClick={() => {
              // Ignore click if the user was just dragging
              if (isDragging.current) return;
              setIsOpen((prev) => !prev);
            }}
            sx={{
              background: 'linear-gradient(135deg, #18333A 0%, #2A5A66 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            }}
          >
            {isOpen ? <CloseIcon /> : <ChatIcon />}
          </Fab>
        </Tooltip>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            style={{
              position: 'absolute',
              bottom: 120,
              right: 20,
              width: '380px',
              height: '500px',
              x,
              y,
              pointerEvents: 'auto',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={6}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(24, 51, 58, 0.1)',
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    p: 2,
                    background: 'linear-gradient(135deg, #18333A 0%, #2A5A66 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
                    <BotIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      Fleet Assistant
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Always active
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setIsOpen(false)}
                    sx={{ ml: 'auto', color: 'white' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Messages Area */}
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    bgcolor: '#f8f9fa',
                  }}
                >
                  {messages.map((msg, index) => (
                    <Box
                      key={index}
                      sx={{
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '85%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                          gap: 1,
                          alignItems: 'flex-end',
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: msg.role === 'user' ? '#2A5A66' : '#18333A',
                            fontSize: '14px',
                          }}
                        >
                          {msg.role === 'user' ? <UserIcon sx={{ fontSize: 14 }} /> : <BotIcon sx={{ fontSize: 14 }} />}
                        </Avatar>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            bgcolor: msg.role === 'user' ? '#18333A' : 'white',
                            color: msg.role === 'user' ? 'white' : 'text.primary',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          }}
                        >
                          <Typography variant="body2">{msg.content}</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              textAlign: msg.role === 'user' ? 'right' : 'left',
                              opacity: 0.6,
                              fontSize: '10px',
                            }}
                          >
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Paper>
                      </Box>
                      {msg.actions && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {msg.actions.map((action, idx) => (
                            <Box
                              key={idx}
                              onClick={() => handleAction(action)}
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '16px',
                                border: '1px solid #18333A',
                                color: '#18333A',
                                fontSize: '12px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: '#18333A',
                                  color: 'white',
                                },
                              }}
                            >
                              {action}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                  {isTyping && (
                    <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} thickness={5} sx={{ color: '#18333A' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Assistant is thinking...
                      </Typography>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Input Area */}
                <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ask about your fleet..."
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '24px',
                          bgcolor: '#f0f2f5',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSend}
                      disabled={!inputValue.trim()}
                      sx={{
                        bgcolor: '#18333A',
                        color: 'white',
                        '&:hover': { bgcolor: '#2A5A66' },
                        '&.Mui-disabled': { bgcolor: '#f0f2f5', color: '#ccc' },
                      }}
                    >
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default FleetChatbot;
